import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";

import { Service } from "utils/services";
import GoogleFontLoader from "react-google-font-loader";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem } from "components/ui/carousel";

import appStore from "./appStore";
import { PostReaction } from "models/postReaction";
import { useWatchTime } from "./useWatchTime";

interface ViewStoryState {
  users: any[];
  excludedUserIds: Set<string>;
  usersAppRecord: AppRecordJK;
  usersStoriesAppRecord: AppRecordJK;

  currentUserIndex: number;
  currentStoryIndex: number;
  storyKey: number;
  isPaused: boolean;

  initializePage: (userId: string | null) => Promise<void>;
  fetchProfileUserStory: (params?: { userId?: string | null; isRefresh?: boolean }) => Promise<void>;

  fetchUsersStoriesByUserIds: (userIds: string[], isRefresh: boolean) => Promise<void>;
  handleNext: (index: number) => void;
  handlePrev: (index: number) => void;
  togglePausePlay: () => void;
  onClicedUserStory: (index: number) => void;
  submitPostReaction: (story: any, reaction: string) => void;
}

const numGetStories = 3;
const isUsingDefaultDuration = false;
const defaultDuration = 5000;

const noStory = {
  content: ({ action, isPaused }: { action: any; isPaused: boolean }) => {
    return (
      <div className="bg-red-500 h-full w-full">
        <h1>Not yet implement.</h1>
      </div>
    );
  },
  duration: defaultDuration,
};

const statusStoryNotValide = {
  content: ({ action, isPaused }: { action: any; isPaused: boolean }) => {
    return (
      <div className="bg-red-500 h-full w-full">
        <h3>Status Content Not aviable→</h3>
      </div>
    );
  },
  duration: defaultDuration,
};

const VideoStory = ({ action, isPaused, post }: { action: any; isPaused: boolean; post: any }) => {
  const { isMuted } = appStore();

  const width = Service.toDouble(post?.post_attachments[0]?.attachment_media_width);
  const height = Service.toDouble(post?.post_attachments[0]?.attachment_media_height);
  const ratio = height === 0 ? 1 : width / height;

  return (
    <div className="flex relative h-full w-full items-center">
      <div
        className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl "
        style={{
          backgroundImage: `url("${post?.post_attachments[0]?.attachment_thumbnail_image_file_src}")`,
        }}
      />

      <video
        src={post?.post_attachments[0]?.attachment_media_image_file_src}
        className={`relative z-10 w-full h-full ${ratio >= 0 && ratio < 0.74 ? "object-cover" : "object-contain"}`}
        playsInline
        style={{ pointerEvents: "auto" }}
        autoPlay
        muted={isMuted}
        ref={(video) => {
          if (video) {
            try {
              isPaused ? video.pause() : video.play();
            } catch (error) {
              AppLog.show({
                message: `[Playback failed]`,
                status: StatusMsg.error,
                error,
              });
            }
          }
        }}
      />
    </div>
  );
};

const createVideoStory = (post: any, duration: number) => ({
  content: (props: { action: any; isPaused: boolean }) => <VideoStory {...props} post={post} />,
  duration: duration,
  post: post,
});

const useViewStoryStore = create<ViewStoryState>((set, get) => ({
  users: [],
  usersStories: [],
  excludedUserIds: new Set<string>(),
  usersAppRecord: new AppRecordJK(),
  usersStoriesAppRecord: new AppRecordJK(),

  currentUserIndex: 0,
  currentStoryIndex: 0,
  storyKey: 0,
  isPaused: false,
  userFetchIndex: 0,

  initializePage: async (userId: string | null) => {
    try {
      set({
        users: [],
        excludedUserIds: new Set<string>(),
        usersAppRecord: new AppRecordJK(),
        usersStoriesAppRecord: new AppRecordJK(),

        currentUserIndex: 0,
        currentStoryIndex: 0,
        storyKey: 0,
        isPaused: false,
      });

      console.log("initializePage");

      await useViewStoryStore
        .getState()
        .fetchProfileUserStory({ userId: userId, isRefresh: true })
        .then(async (_) => {
          const users = get().users;
          const userIdsArray: string[] = users.slice(0, numGetStories).map((user) => `${user.id}`);
          await useViewStoryStore.getState().fetchUsersStoriesByUserIds(userIdsArray, true);
        });
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchProfileUserStory: async ({
    userId = null,
    isRefresh = false,
  }: Partial<{
    userId: string | null;
    isRefresh: boolean;
  }> = {}) => {
    const { users, excludedUserIds, usersAppRecord } = get();

    let tmpUsers = [...users];
    let tmpExcludedUserIds = new Set(excludedUserIds);
    let tmpUsersAppRecord = new AppRecordJK({
      ...usersAppRecord,
    });

    try {
      let url: string = "";
      url = RouteApi.getProfileUserStoryWeb;
      url += `token=${Service.token}`;

      if (isRefresh) {
        tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.loading;
        tmpUsers = [];
        set({
          users: tmpUsers,
          usersAppRecord: tmpUsersAppRecord,
          excludedUserIds: tmpExcludedUserIds,
        });
      } else {
        if (get().usersAppRecord.canLoadMore()) {
          if (tmpUsersAppRecord.canLoadMore()) {
            tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
            set({ usersAppRecord: tmpUsersAppRecord });
          } else {
            return;
          }
        } else {
          return;
        }
      }

      const excludedUserIdsArray: string[] = [...tmpExcludedUserIds];
      const data = {
        user_id: userId,
        excluded_user_ids: excludedUserIdsArray,
      };

      const responses = await RequestApi(url, { data: data });
      if (responses.status === "success") {
        const newUsers: any[] = responses.users;

        if (newUsers && newUsers.length > 0) {
          tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }

        const filteredUsers = newUsers.filter((user) => !excludedUserIds.has(`${user.id}`));

        filteredUsers.forEach((user) => tmpExcludedUserIds.add(`${user.id}`));

        set({
          usersAppRecord: tmpUsersAppRecord,
          users: [...tmpUsers, ...newUsers],
          excludedUserIds: tmpExcludedUserIds,
        });
      } else {
        tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ usersAppRecord: tmpUsersAppRecord });
      }
    } catch (error) {
      tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ usersAppRecord: tmpUsersAppRecord });

      AppLog.show({
        message: `[fetchProfileUserStory]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchUsersStoriesByUserIds: async (userIds: string[], isRefresh: boolean) => {
    const { users, usersStoriesAppRecord } = get();

    let tmpUsers: any[] = [...users];
    let tmpUsersStoriesAppRecord = new AppRecordJK({
      ...usersStoriesAppRecord,
    });

    try {
      let url: string = "";
      url = RouteApi.getProfilePostUserStoryWeb;
      url += `token=${Service.token}`;

      if (!isRefresh) {
        if (tmpUsersStoriesAppRecord.canLoadMore()) {
          tmpUsersStoriesAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ usersStoriesAppRecord: tmpUsersStoriesAppRecord });
        } else {
          return;
        }
      }

      const data = {
        user_ids: userIds,
      };
      const responses = await RequestApi(url, { data: data });
      if (responses.status === "success") {
        const newUsers: any[] = structuredClone(responses.users);

        for (const userId of userIds) {
          const user = newUsers.find((user) => `${user.id}` === userId);

          if (user) {
            if (user.posts && user.posts.length > 0) {
              const newStories: any[] = [];
              for (const post of user.posts) {
                if (post.media_type?.toLowerCase() === "photo") {
                  const width = Service.toDouble(post?.post_attachments[0].attachment_media_width);
                  const height = Service.toDouble(post?.post_attachments[0].attachment_media_height);
                  const ratio = height === 0 ? 1 : width / height;

                  let duration = defaultDuration;
                  if (post?.sound_url) {
                    duration = Service.toInteger(post.sound_duration) * 1000;
                    if (!duration) {
                      console.log("duration have zero : ", duration);
                      duration = defaultDuration;
                    }
                  }

                  const photoStory = {
                    content: ({ action, isPaused }: { action: any; isPaused: boolean }) => {
                      return (
                        <div className="flex w-full h-full relative overflow-hidden mt-2">
                          <div
                            className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl"
                            style={{
                              backgroundImage: `url("${post?.post_attachments[0]?.attachment_thumbnail_image_file_src}")`,
                            }}
                          />

                          <img
                            src={post.post_attachments[0].attachment_media_image_file_src}
                            alt="Post"
                            className={`relative z-10  w-full h-auto  ${ratio >= 0 && ratio < 0.74 ? "object-cover" : "object-contain"}`}
                            loading="lazy"
                          />
                        </div>
                      );
                    },
                    duration: isUsingDefaultDuration ? defaultDuration : duration,
                    post: post,
                  };
                  newStories.push(photoStory);
                } else if (post.media_type?.toLowerCase() === "album") {
                  let duration = 5000;
                  if (post?.post_attachments && post?.post_attachments?.length > 0) {
                    duration = Service.toInteger(post?.post_attachments?.length) * 5000;
                    if (!duration) {
                      console.log("duration have zero : ", duration);
                      duration = defaultDuration;
                    }
                  }

                  const albumStory = {
                    content: ({ action, isPaused }: { action: any; isPaused: boolean }) => {
                      return (
                        <div className="flex h-full w-full items-center relative">
                          <div
                            className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl"
                            style={{
                              backgroundImage: `url("${post?.post_attachments[0].attachment_thumbnail_image_file_src}")`,
                            }}
                          />
                          <Carousel
                            plugins={[
                              Autoplay({
                                delay: 5000,
                              }),
                            ]}
                            className="flex w-full h-full items-center"
                          >
                            <CarouselContent className="">
                              {post?.post_attachments.map((att: any, index: number) => (
                                <CarouselItem key={index}>
                                  <div className="flex w-full h-full relative ">
                                    <img src={att.attachment_media_image_file_src} alt="Post" className="relative z-10  w-full h-auto  object-contain" loading="lazy" />
                                  </div>
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                          </Carousel>
                        </div>
                      );
                    },
                    duration: isUsingDefaultDuration ? defaultDuration : duration,
                    post: post,
                  };
                  newStories.push(albumStory);
                } else if (post.media_type?.toLowerCase() === "video") {
                  let duration = defaultDuration;
                  duration = Service.toInteger(post?.video_duration) * 1000;
                  if (!duration) {
                    console.log("duration have zero : ", duration);
                    duration = 5000;
                  } else {
                    duration += 1000;
                  }

                  const videoStory = createVideoStory(post, isUsingDefaultDuration ? defaultDuration : duration);

                  newStories.push(videoStory);
                } else if (post.media_type?.toLowerCase() === "status") {
                  let textPost = JSON.parse(post?.media_style);

                  if (!textPost) {
                    newStories.push(statusStoryNotValide);
                  } else {
                    let subStringBackgroundColor: string = textPost?.background_color;
                    let subStringColor: string = textPost?.text_style?.color;

                    if (subStringBackgroundColor.length && subStringColor.length === 8) {
                      subStringBackgroundColor = subStringBackgroundColor.substring(2);
                      subStringColor = subStringColor.substring(2);
                    } else {
                      subStringColor = "ffffff";
                      subStringBackgroundColor = "000000";
                    }

                    const textSize = textPost?.text_style?.font_size * 0.65;

                    let duration = defaultDuration;
                    if (post?.sound_url) {
                      duration = Service.toInteger(post.sound_duration) * 1000;
                      if (!duration) {
                        console.log("duration have zero : ", duration);
                        duration = defaultDuration;
                      }
                    }

                    const statusStory = {
                      content: ({ action, isPaused }: { action: any; isPaused: boolean }) => {
                        return (
                          <div
                            style={{
                              overflow: "hidden",
                              backgroundColor: `#${subStringBackgroundColor}`,
                            }}
                            className="flex w-full h-full items-center text-center justify-center"
                          >
                            <GoogleFontLoader
                              fonts={[
                                {
                                  font: `${textPost?.text_style?.font}`,
                                  weights: [400],
                                },
                              ]}
                            />
                            <p
                              style={{
                                color: `#${subStringColor}`,
                                fontSize: textSize,
                                fontFamily: `${textPost?.text_style?.font}, sans-serif`,
                              }}
                              className="text-center whitespace-pre-line"
                            >
                              {textPost?.text}
                            </p>
                          </div>
                        );
                      },
                      duration: duration,
                      post: post,
                    };

                    newStories.push(statusStory);
                  }
                } else {
                  newStories.push(noStory);
                }
              }

              const indexUser = tmpUsers.findIndex((element) => `${element.id}` === userId);
              if (indexUser > -1) {
                tmpUsers[indexUser] = {
                  ...tmpUsers[indexUser],
                  stories: newStories,
                };
              }
            }
          }
        }

        if (newUsers && newUsers.length > 0) {
          tmpUsersStoriesAppRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpUsersStoriesAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }

        set({
          users: tmpUsers,
          usersStoriesAppRecord: tmpUsersStoriesAppRecord,
        });
      } else {
        tmpUsersStoriesAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ usersStoriesAppRecord: tmpUsersStoriesAppRecord });
      }
    } catch (error) {
      tmpUsersStoriesAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ usersStoriesAppRecord: tmpUsersStoriesAppRecord });

      AppLog.show({
        message: `[fetchUsersStoriesByUserIds]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  handleNext: async (index: number) => {
    try {
      const { users, currentUserIndex, currentStoryIndex, storyKey } = get();

      let tmpCurrentUserIndex = currentUserIndex;
      let tmpCurrentStoryIndex = currentStoryIndex;
      let tmpStoryKey = storyKey;

      const currentUser = users[tmpCurrentUserIndex];
      if (index < currentUser.stories.length - 1) {
        tmpCurrentStoryIndex += 1;
        tmpStoryKey += 1;

        set({
          currentStoryIndex: tmpCurrentStoryIndex,
          storyKey: tmpStoryKey,
        });
      } else if (currentUserIndex < users.length - 1) {
        tmpCurrentUserIndex += 1;
        tmpCurrentStoryIndex = 0;
        tmpStoryKey += 1;

        set({
          currentUserIndex: tmpCurrentUserIndex,
          currentStoryIndex: tmpCurrentStoryIndex,
          storyKey: tmpStoryKey,
        });

        if (!users[tmpCurrentUserIndex].stories) {
          await useViewStoryStore.getState().fetchUsersStoriesByUserIds([`${users[tmpCurrentUserIndex].id}`], false);
        }

        if (tmpCurrentUserIndex + 1 <= users.length - 1) {
          if (!users[tmpCurrentUserIndex + 1].stories) {
            await useViewStoryStore.getState().fetchUsersStoriesByUserIds([`${users[tmpCurrentUserIndex + 1].id}`], false);
          }

          if (tmpCurrentUserIndex + 1 > users.length - 2) {
            await useViewStoryStore.getState().fetchProfileUserStory({ isRefresh: false });
          }
        }
      } else {
        useWatchTime.getState().setActivePost(null);
        console.log("end of story");
      }
    } catch (error) {
      AppLog.show({
        message: `[handleNext]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  handlePrev: (index: number | undefined) => {
    try {
      const { users, currentUserIndex, currentStoryIndex, storyKey } = get();

      let tmpCurrentUserIndex = currentUserIndex;
      let tmpCurrentStoryIndex = currentStoryIndex;
      let tmpStoryKey = storyKey;

      const validIndex = index !== undefined ? index : currentStoryIndex;

      if (validIndex > 0) {
        tmpCurrentStoryIndex -= 1;
        tmpStoryKey -= 1;

        set({
          currentStoryIndex: tmpCurrentStoryIndex,
          storyKey: tmpStoryKey,
        });
      } else if (currentUserIndex > 0) {
        tmpCurrentUserIndex -= 1;
        tmpStoryKey -= 1;

        if (!users[tmpCurrentUserIndex].stories) {
          useViewStoryStore.getState().fetchUsersStoriesByUserIds([`${users[tmpCurrentUserIndex].id}`], false);

          set({
            currentUserIndex: tmpCurrentUserIndex,
            currentStoryIndex: 0,
            storyKey: tmpStoryKey,
          });
        } else {
          const prevUser = users[tmpCurrentUserIndex];
          tmpCurrentStoryIndex = prevUser.stories.length - 1;

          set({
            currentUserIndex: tmpCurrentUserIndex,
            currentStoryIndex: tmpCurrentStoryIndex,
            storyKey: tmpStoryKey,
          });
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[handlePrev]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  togglePausePlay: () => {
    try {
      const { isPaused } = get();
      set({
        isPaused: !isPaused,
      });
    } catch (error) {
      AppLog.show({
        message: `[togglePausePlay]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClicedUserStory: async (index: number) => {
    try {
      const { users } = get();
      set({ currentUserIndex: index });

      if (index + 1 > users.length - 2) {
        await useViewStoryStore.getState().fetchProfileUserStory({ isRefresh: false });
      }

      if (!users[index].stories) {
        const userIdsArray: string[] = get()
          .users.slice(index, index + numGetStories)
          .map((user) => `${user.id}`);

        await useViewStoryStore.getState().fetchUsersStoriesByUserIds(userIdsArray, false);
      }
    } catch (error) {
      AppLog.show({
        message: `[onClicedUserStory]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  submitPostReaction: async (story: any, reaction: string) => {
    try {
      let url = RouteApi.submitPostReaction;
      url += `token=${Service.token}`;

      const synchId = await Service.getSynchID();
      const now = new Date();

      const postReactionData = {
        post_reactions: [
          {
            user_id: story.user_id,
            reaction_user_id: Service.userAuth.id,
            post_id: story.provider_post_id,
            attachment_id: story?.post_attachments[0]?.attachment_id ?? "",
            provider_reaction_id: synchId,
            reaction_datetime: Service.toDateTimeStr(now),
            reaction_type: PostReaction.like.value,
            reaction_status: "Yes",
            // reaction_second: watchDuration,
            send_to_user_id: "",
            share_to_user_id: "",
            share_to_provider: "",
            share_to_provider_account_id: "",
            created_by: Service.userAuth.name,
            created_at: Service.toDateTimeStr(now),
            synch_unid: synchId,
            synch_by: Service.userAuth.name,
            synch_status: "New",
            synch_at: Service.toDateTimeStr(now),
            emotion_type: reaction,
          },
        ],
      };

      const responses = await RequestApi(url, { data: postReactionData });
      if (responses.status === "success") {
        AppLog.show({
          message: `${responses["msg"]}`,
          status: StatusMsg.getType(responses["status"]),
        });
      } else {
        AppLog.show({
          message: `[submitViewReaction] ${responses["msg"]}`,
          status: StatusMsg.getType(responses["status"]),
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[submitPostReaction]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

export default useViewStoryStore;

